/**
 * @file Components/Admin/Testimonials.jsx
 * @description Admin testimonial management with full CRUD, image upload,
 * rating selector, approve/reject toggle, search/filter, and auto-refresh.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, Star, Trash2, Check, X, Plus, Edit, Loader2, RefreshCw, MessageSquare, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from '../../services/api';
import ImageUploader from '../Ui/ImageUploader';

const emptyForm = { name: '', location: '', image: '', review: '', rating: 5, approved: false };

const RatingPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n)}
        className="focus:outline-none">
        <Star size={20} className={n <= value ? 'text-amber-400 fill-current' : 'text-gray-300'} />
      </button>
    ))}
    <span className="text-xs text-gray-500 ml-1">{value}/5</span>
  </div>
);

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAllTestimonials();
      const list = res?.data?.data?.testimonials || res?.data?.testimonials || res?.data || [];
      setTestimonials(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTestimonials(); }, []);

  // Auto-refresh every 30s
  useEffect(() => {
    const interval = setInterval(fetchTestimonials, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.location.trim() || !formData.review.trim()) {
      toast.error('Name, location and review are required');
      return;
    }
    setSaving(true);
    try {
      if (selectedItem) {
        await updateTestimonial(selectedItem._id, formData);
        toast.success('Testimonial updated');
      } else {
        await createTestimonial(formData);
        toast.success('Testimonial created');
      }
      fetchTestimonials();
      closeModal();
    } catch (err) {
      toast.error('Failed to save: ' + (err?.response?.data?.message || err?.message));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleApproval = async (item) => {
    try {
      await updateTestimonial(item._id, { approved: !item.approved });
      toast.success(item.approved ? 'Testimonial unapproved' : 'Testimonial approved');
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete testimonial from "${item.name}"?`)) return;
    try {
      await deleteTestimonial(item._id);
      toast.success('Testimonial deleted');
      fetchTestimonials();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || '',
      location: item.location || '',
      image: item.image || '',
      review: item.review || '',
      rating: item.rating || 5,
      approved: item.approved || false,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setFormData({ ...emptyForm });
  };

  const setF = (field, value) => setFormData(p => ({ ...p, [field]: value }));

  const filtered = useMemo(() => {
    return testimonials.filter(t => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q ||
        (t.name || '').toLowerCase().includes(q) ||
        (t.review || '').toLowerCase().includes(q) ||
        (t.location || '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'approved' && t.approved) ||
        (statusFilter === 'pending' && !t.approved);
      return matchesSearch && matchesStatus;
    });
  }, [testimonials, searchQuery, statusFilter]);

  const approvedCount = testimonials.filter(t => t.approved).length;
  const pendingCount = testimonials.filter(t => !t.approved).length;

  if (loading && testimonials.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin h-8 w-8 text-indigo-500" />
      </div>
    );
  }

  if (error && testimonials.length === 0) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="text-red-500">{error}</p>
        <button onClick={fetchTestimonials} className="text-sm text-indigo-600 hover:underline flex items-center gap-1 mx-auto">
          <RefreshCw size={14} /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Total</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{testimonials.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Approved</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{approvedCount}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-xs text-gray-500 uppercase font-medium">Pending</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{pendingCount}</p>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" placeholder="Search by name, review, location..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-72" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs text-gray-400">Auto-refreshes every 30s</p>
          <button onClick={fetchTestimonials} disabled={loading}
            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={() => { setSelectedItem(null); setFormData({ ...emptyForm }); setIsModalOpen(true); }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-700 text-sm font-medium">
            <Plus size={16} /> Add Testimonial
          </button>
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length > 0 ? (
          filtered.map((t) => (
            <div key={t._id} className={`bg-white rounded-xl shadow-sm border p-5 transition-all ${
              !t.approved ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-emerald-400'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  {t.image ? (
                    <img src={t.image} alt={t.name}
                      className="h-11 w-11 rounded-full object-cover border-2 border-gray-100"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-indigo-600 font-semibold">{t.name?.charAt(0)?.toUpperCase() || '?'}</span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.location}</p>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  t.approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {t.approved ? 'Approved' : 'Pending'}
                </span>
              </div>

              <div className="flex items-center gap-0.5 mb-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} size={13} className={n <= (t.rating || 5) ? 'text-amber-400 fill-current' : 'text-gray-200'} />
                ))}
              </div>

              <p className="text-sm text-gray-600 line-clamp-3 mb-3">{t.review}</p>

              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-[10px] text-gray-400">
                  {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                </span>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => handleToggleApproval(t)}
                    className={`p-1.5 rounded-lg transition-colors text-xs font-medium flex items-center gap-1 ${
                      t.approved
                        ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`} title={t.approved ? 'Unapprove' : 'Approve'}>
                    {t.approved ? <><X size={12} /> Unapprove</> : <><Check size={12} /> Approve</>}
                  </button>
                  <button onClick={() => handleEdit(t)}
                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => handleDelete(t)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-16">
            <MessageSquare size={40} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-gray-500">
              {searchQuery || statusFilter !== 'all' ? 'No matching testimonials found' : 'No testimonials yet. Add one!'}
            </p>
          </div>
        )}
      </div>

      {filtered.length > 0 && (
        <p className="text-xs text-gray-400 text-center">Showing {filtered.length} of {testimonials.length} testimonials</p>
      )}

      {/* ═══════ Create/Edit Modal ═══════ */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-lg w-full my-8 shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">{selectedItem ? 'Edit Testimonial' : 'Add Testimonial'}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>

            <form onSubmit={handleSave} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input type="text" value={formData.name} onChange={(e) => setF('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <input type="text" value={formData.location} onChange={(e) => setF('location', e.target.value)}
                    placeholder="e.g. Mumbai, India" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-indigo-500" required />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <RatingPicker value={formData.rating} onChange={(v) => setF('rating', v)} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Review *</label>
                <textarea value={formData.review} onChange={(e) => setF('review', e.target.value)}
                  rows={4} placeholder="Customer's review text..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none resize-none focus:ring-1 focus:ring-indigo-500" required />
              </div>

              <ImageUploader label="Customer Photo" value={formData.image} onChange={(v) => setF('image', v)}
                hint="Upload customer photo or paste URL. Optional." />

              <div className="flex items-center gap-2">
                <input type="checkbox" id="testimonial-approved" checked={formData.approved}
                  onChange={(e) => setF('approved', e.target.checked)} className="rounded" />
                <label htmlFor="testimonial-approved" className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <CheckCircle size={14} className="text-emerald-500" /> Approved (visible on website)
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 flex items-center gap-2">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : (selectedItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonials;